import React, { useState, useEffect } from 'react'
import './AnalyticsDashboard.css'

/**
 * 유튜브 채널 분석 대시보드
 */
function AnalyticsDashboard() {
  const [selectedPeriod, setSelectedPeriod] = useState('7days')
  const [channelData, setChannelData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  // 더미 데이터 (실제로는 YouTube API 연동)
  const dummyData = {
    channel: {
      name: '내 채널',
      subscribers: 15420,
      subscriberChange: 234,
      totalViews: 1245600,
      totalVideos: 127,
      avgViews: 9807
    },
    recentVideos: [
      { id: 1, title: '최신 영상 제목 1', views: 12500, likes: 450, comments: 67, ctr: 8.2 },
      { id: 2, title: '최신 영상 제목 2', views: 8900, likes: 320, comments: 45, ctr: 6.5 },
      { id: 3, title: '최신 영상 제목 3', views: 15200, likes: 580, comments: 89, ctr: 9.1 },
      { id: 4, title: '최신 영상 제목 4', views: 6700, likes: 210, comments: 32, ctr: 5.8 },
      { id: 5, title: '최신 영상 제목 5', views: 11300, likes: 390, comments: 56, ctr: 7.3 }
    ],
    topKeywords: [
      { keyword: '튜토리얼', count: 45, trend: 'up' },
      { keyword: '리뷰', count: 38, trend: 'up' },
      { keyword: '초보자', count: 32, trend: 'stable' },
      { keyword: '가이드', count: 28, trend: 'down' },
      { keyword: '팁', count: 25, trend: 'up' }
    ],
    competitorChannels: [
      { name: '경쟁 채널 A', subscribers: 25000, growth: 12.5 },
      { name: '경쟁 채널 B', subscribers: 18000, growth: 8.3 },
      { name: '경쟁 채널 C', subscribers: 22000, growth: 15.2 }
    ]
  }

  useEffect(() => {
    // 초기 데이터 로드
    setChannelData(dummyData)
  }, [])

  const periodOptions = [
    { value: '7days', label: '최근 7일' },
    { value: '30days', label: '최근 30일' },
    { value: '90days', label: '최근 90일' },
    { value: 'year', label: '최근 1년' }
  ]

  return (
    <div className="analytics-dashboard">
      {/* 대시보드 헤더 */}
      <div className="dashboard-header">
        <h2>유튜브 채널 분석</h2>
        <div className="period-selector">
          <select 
            value={selectedPeriod} 
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="period-select"
          >
            {periodOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* 채널 개요 카드 */}
      <div className="overview-cards">
        <div className="stat-card">
          <div className="stat-icon subscribers">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div className="stat-content">
            <h3>구독자</h3>
            <div className="stat-value">{channelData?.channel.subscribers.toLocaleString()}</div>
            <div className="stat-change positive">
              +{channelData?.channel.subscriberChange} ({selectedPeriod})
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon views">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </div>
          <div className="stat-content">
            <h3>총 조회수</h3>
            <div className="stat-value">{channelData?.channel.totalViews.toLocaleString()}</div>
            <div className="stat-subtext">평균 {channelData?.channel.avgViews.toLocaleString()} 조회</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon videos">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <polygon points="23 7 16 12 23 17 23 7" />
              <rect x="1" y="5" width="15" height="14" rx="2" />
            </svg>
          </div>
          <div className="stat-content">
            <h3>총 영상</h3>
            <div className="stat-value">{channelData?.channel.totalVideos}</div>
            <div className="stat-subtext">업로드된 영상</div>
          </div>
        </div>
      </div>

      {/* 최근 영상 성과 */}
      <div className="recent-videos-section">
        <h3>최근 영상 성과</h3>
        <div className="videos-table">
          <div className="table-header">
            <span>영상 제목</span>
            <span>조회수</span>
            <span>좋아요</span>
            <span>댓글</span>
            <span>CTR</span>
          </div>
          {channelData?.recentVideos.map(video => (
            <div key={video.id} className="table-row">
              <span className="video-title">{video.title}</span>
              <span>{video.views.toLocaleString()}</span>
              <span>{video.likes}</span>
              <span>{video.comments}</span>
              <span className="ctr-badge">{video.ctr}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* 트렌딩 키워드 & 경쟁 채널 */}
      <div className="analytics-grid">
        {/* 트렌딩 키워드 */}
        <div className="trending-keywords">
          <h3>트렌딩 키워드</h3>
          <div className="keyword-list">
            {channelData?.topKeywords.map((item, index) => (
              <div key={index} className="keyword-item">
                <span className="keyword-rank">#{index + 1}</span>
                <span className="keyword-text">{item.keyword}</span>
                <span className="keyword-count">{item.count}회</span>
                <span className={`trend-icon ${item.trend}`}>
                  {item.trend === 'up' && '↑'}
                  {item.trend === 'down' && '↓'}
                  {item.trend === 'stable' && '→'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* 경쟁 채널 분석 */}
        <div className="competitor-analysis">
          <h3>경쟁 채널 분석</h3>
          <div className="competitor-list">
            {channelData?.competitorChannels.map((channel, index) => (
              <div key={index} className="competitor-item">
                <div className="competitor-info">
                  <span className="competitor-name">{channel.name}</span>
                  <span className="competitor-subs">
                    {channel.subscribers.toLocaleString()} 구독자
                  </span>
                </div>
                <div className="growth-indicator">
                  <span className="growth-value">+{channel.growth}%</span>
                  <div className="growth-bar">
                    <div 
                      className="growth-fill" 
                      style={{ width: `${Math.min(channel.growth * 5, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* AI 인사이트 */}
      <div className="ai-insights">
        <h3>AI 인사이트</h3>
        <div className="insight-cards">
          <div className="insight-card">
            <div className="insight-icon">💡</div>
            <div className="insight-content">
              <h4>최적 업로드 시간</h4>
              <p>화요일, 목요일 오후 7-9시에 업로드한 영상이 가장 높은 조회수를 기록했습니다.</p>
            </div>
          </div>
          <div className="insight-card">
            <div className="insight-icon">📈</div>
            <div className="insight-content">
              <h4>성장 기회</h4>
              <p>'튜토리얼' 키워드를 포함한 영상이 평균보다 45% 높은 CTR을 보입니다.</p>
            </div>
          </div>
          <div className="insight-card">
            <div className="insight-icon">🎯</div>
            <div className="insight-content">
              <h4>콘텐츠 추천</h4>
              <p>쇼츠 형식의 영상을 더 제작하면 구독자 증가율이 개선될 것으로 예상됩니다.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AnalyticsDashboard